import React, { useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import {
  ProgressBarContainer,
  ProgressSection,
  TopBar,
  FrameForDataContainer,
} from "./index";
import { Button } from "@vds/buttons";
import { Input, TextArea } from "@vds/inputs";
import { useFormikContext } from "formik";
import FloorPlanTileEdit from "./FloorPlanTileEdit";
import {
  Cell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@vds/tables";
import SiteBuilderTile from "./SiteBuilderTile";
import { Body } from "@vds/typography";

const ReviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 750px;
  width: 100%;
  max-height: 500px;
  margin: 24px 10px 24px 10px;
  overflow-y: auto;
`;

const ProgressAndButtonContainer = styled.div`
  width: 70%;
  height: auto;
  display: flex;
  align-items: center;
  gap: 70px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
`;

const FloorPlanTilesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  min-width: 60%;
  max-height: 500px;
  overflow-y: auto;
  padding: 16px 0;
  white-space: nowrap;
`;

const ReviewUsersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  width: 100%;
  max-height: 500px;
  min-width: 60%;
  height: 100%;
  white-space: nowrap;
`;

const ReviewSiteBuilderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  width: 100%;
  max-width: 100%;
  min-width: 60%;
  height: 100%;
  max-height: 500px;
  white-space: nowrap;
`;

const ReviewBasicInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  /* overflow-y: auto; */
  width: 100%;
  /* height: 100%;
  max-height: 78%;
  min-height: 20%; */
  white-space: nowrap;
`;

const EditButtonContainer = styled.div`
  flex-shrink: 0;
  max-width: 180px;
  width: 100%;
  white-space: nowrap;
  @media (max-width: 1024px) {
    max-width: 100%;
    width: auto;
  }
`;

export const TitleOfContent = styled.h2`
  color: #000;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Verizon-NHG-eDS;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  height: auto;
`;

const sections = ["Site Information", "Floor Plan", "Site Builder", "Users"];

export const ReviewFormatter = (ReviewComponent) => (props) => {
  const { count, handleEditClick, getButtonText, ...restProps } = props;
  return (
    <>
      <TitleOfContent>{sections[count]}</TitleOfContent>
      <ReviewComponent {...restProps} />
      <EditButtonContainer>
        {" "}
        <Button
          size="small"
          disabled={false}
          use="secondary"
          data-testid="reviewPageNew_editButtonClick"
          onClick={() => handleEditClick(count)}
        >
          {getButtonText(count)}
        </Button>
      </EditButtonContainer>
      <hr></hr>
    </>
  );
};

function ReviewPageNew({
  currentFormIndex,
  setCurrentFormIndex,
  setFromReviewPage,
}) {
  const location = useLocation();
  const { values } = useFormikContext();
  const mode = "edit";
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const handleSectionClick = (index) => {
    setCurrentSectionIndex(index);
  };

  const handleEditClick = (index) => {
    setCurrentFormIndex(index);
    setFromReviewPage(true);
  };

  const getButtonText = (currentSectionIndex) => {
    return `Edit ${sections[currentSectionIndex]}`;
  };

  const renderBasicInfoInputs = () => (
    <ReviewBasicInfoContainer>
      {values?.campusName && (
        <Input
          width="100%"
          type="text"
          label="Campus Name"
          value={values.campusName}
          readOnly={true}
        />
      )}
      <Input
        width="100%"
        type="text"
        label="Site Name"
        value={values?.siteName}
        readOnly={true}
      />
      <Input
        width="100%"
        type="text"
        label="State"
        value={values?.state?.label}
        readOnly={true}
      />
      <Input
        width="100%"
        type="text"
        label="City"
        value={values?.city?.label}
        readOnly={true}
      />
      <TextArea
        label="Site Description"
        readOnly={true}
        value={values?.siteDescription || null}
        width="100%"
        required={!!values?.siteDescription}
      />
    </ReviewBasicInfoContainer>
  );

  const renderFloorPlanInputs = () => (
    <div>
      {values?.files?.length
        ? values?.files.map((fileObj, index) => (
            <FloorPlanTileEdit
              key={index}
              file={fileObj}
              index={index}
              isEditable={false}
              mode={mode}
              fileTypes={["JPG", "PNG", "JPEG"]}
              maxSize={10}
              handleFileChange={() => {}}
              reviewPage={true}
            />
          ))
        : null}
    </div>
  );

  const renderSiteBuilderInputs = () => (
    <div>
      <SiteBuilderTile reviewPage={true} />
    </div>
  );

  const renderUserInputs = () => (
    <div>
      <Table striped={false} bottomLine="secondary" padding="compact">
        <TableHead bottomLine="primary">
          <TableHeader>User name</TableHeader>
          <TableHeader>Email</TableHeader>
        </TableHead>

        <TableBody>
          {values.users?.map((user, index) => (
            <TableRow key={index}>
              <Cell>
                {user?.firstname} {user?.lastname}
              </Cell>
              <Cell>{user.email}</Cell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderInputsForCurrentTab = () => {
    switch (currentSectionIndex) {
      case 0:
        return renderBasicInfoInputs();
      case 1:
        return renderFloorPlanInputs();
      case 2:
        return renderSiteBuilderInputs();
      case 3:
        return renderUserInputs();
      default:
        return null;
    }
  };

  const BasicInfoWithFormatter = ReviewFormatter(
    ({ count, handleEditClick, getButtonText }) => renderBasicInfoInputs()
  );
  const FloorPlansWithFormatter = ReviewFormatter(
    ({ count, handleEditClick, getButtonText }) => renderFloorPlanInputs()
  );
  const SiteBuilderWithFormatter = ReviewFormatter(
    ({ count, handleEditClick, getButtonText }) => renderSiteBuilderInputs()
  );
  const UserInputsWithFormatter = ReviewFormatter(
    ({ count, handleEditClick, getButtonText }) => renderUserInputs()
  );

  return (
    <ReviewWrapper>
      {/* <ProgressAndButtonContainer>
        <ProgressBarContainer
          mode={mode}
          style={{
            width: "100%",
            borderBottom: "2px solid #D8DADA",
            minWidth: "90%",
          }}
        >
          {sections.map((section, index) => (
            <ProgressSection
              key={index}
              onClick={() => handleSectionClick(index)}
              mode={mode}
              disabled={index > currentSectionIndex}
              isActive={index === currentSectionIndex}
              style={{ cursor: "pointer", margin: "0px" }}
            >
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                <Body
                  bold={true}
                  size="large"
                  color={index === currentSectionIndex ? "#000" : "#6F7171"}
                >
                  {section}
                </Body>
              </div>
              <TopBar
                index={index}
                currentFormIndex={currentSectionIndex}
                mode={mode}
                overrideStyle={true}
              />
            </ProgressSection>
          ))}
        </ProgressBarContainer>
        <EditButtonContainer>
          {" "}
          <Button
            size="small"
            disabled={false}
            use="secondary"
            onClick={() => handleEditClick(currentSectionIndex)}
          >
            {getButtonText()}
          </Button>
        </EditButtonContainer>
      </ProgressAndButtonContainer>
      <FrameForDataContainer>
        {renderInputsForCurrentTab()}
      </FrameForDataContainer> */}
      <BasicInfoWithFormatter
        count={0}
        handleEditClick={handleEditClick}
        getButtonText={getButtonText}
      />
      <FloorPlansWithFormatter
        count={1}
        handleEditClick={handleEditClick}
        getButtonText={getButtonText}
      />
      <SiteBuilderWithFormatter
        count={2}
        handleEditClick={handleEditClick}
        getButtonText={getButtonText}
      />
      <UserInputsWithFormatter
        count={3}
        handleEditClick={handleEditClick}
        getButtonText={getButtonText}
      />
    </ReviewWrapper>
  );
}

export default ReviewPageNew;
